import React, { useEffect, useState } from "react";
import "../styles/shared-ui.css";
import "../styles/asset-task-ui.css";
import { useAppContext } from "../context/AppContext";
import {
  createAssetTask,
  getProcessTasks,
  getAssetTasksByProcessId,
} from "../services/assetTaskService";
import { getTasksByCompanyId } from "../services/taskService";
import { getProcessesByCompanyId } from "../services/processService";
import { getCompaniesLite } from "../services/companyService";
import { toast } from "../utils/toast";
import { resolveRoleName } from "../utils/auth";

const AssetTaskCreatePage = () => {
  const { setActiveSection } = useAppContext();
  const [roleChecked, setRoleChecked] = useState(false);
  const [canAccess, setCanAccess] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [chainTasks, setChainTasks] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [loading, setLoading] = useState(true); // initial page load
  const [companyLoading, setCompanyLoading] = useState(false);

  const [selectedCompanyId, setSelectedCompanyId] = useState("");

  const [form, setForm] = useState({
    task_id: "",
    process_id: "",
    type: "information",
    position: "input", // input | output | both
    description: "",
    document_link: "",
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let user = null;
        try {
          const raw = sessionStorage.getItem("auth");
          const auth = raw ? JSON.parse(raw) : null;
          user = auth?.user || null;
        } catch {}
        const rn = user ? await resolveRoleName(user) : null;
        const norm = String(rn || "").toUpperCase();
        const allowed = norm === "SUPER_ADMIN" || norm === "SUPERADMIN";
        if (!cancelled) {
          setCanAccess(allowed);
          setRoleChecked(true);
        }
        if (!allowed) setActiveSection("dashboard");
      } catch {
        if (!cancelled) {
          setCanAccess(false);
          setRoleChecked(true);
        }
        setActiveSection("dashboard");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setActiveSection]);

  // Step-through chain creation
  const [currentIndex, setCurrentIndex] = useState(0); // 0-based in chain
  const [currentPhase, setCurrentPhase] = useState("input"); // "input" | "output"
  const [extraInputEnabled, setExtraInputEnabled] = useState(false);
  const [extraInput, setExtraInput] = useState({
    type: "information",
    description: "",
    document_link: "",
  });

  // Coverage of existing assets for the selected process
  const [processCoverage, setProcessCoverage] = useState({
    total: 0,
    inputCount: 0,
    outputCount: 0,
    completeCount: 0,
    isComplete: false,
  });

  // Modal for confirming chain link and extra input
  const [showChainModal, setShowChainModal] = useState(false);
  const [pendingExtraInputInModal, setPendingExtraInputInModal] =
    useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const companiesData = await getCompaniesLite();
        setCompanies(Array.isArray(companiesData) ? companiesData : []);
      } catch (e) {
        toast.error(e?.message || "Failed to load companies");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleCompanyChange = async (companyId) => {
    setSelectedCompanyId(companyId);
    setField("process_id", "");
    setField("task_id", "");
    setChainTasks([]);

    if (!companyId) {
      setProcesses([]);
      setAllTasks([]);
      return;
    }

    try {
      setCompanyLoading(true);
      const processesData = await getProcessesByCompanyId(companyId);
      setProcesses(Array.isArray(processesData) ? processesData : []);

      const tasksData = await getTasksByCompanyId(companyId);
      setAllTasks(Array.isArray(tasksData) ? tasksData : []);
    } catch (e) {
      toast.error(e?.message || "Failed to load company data");
    } finally {
      setCompanyLoading(false);
    }
  };

  const handleProcessChange = async (processId) => {
    setField("process_id", processId);

    if (!processId) {
      setChainTasks([]);
      setField("task_id", "");
      setField("position", "input");
      setCurrentIndex(0);
      setCurrentPhase("input");
      setExtraInputEnabled(false);
      setExtraInput({
        type: "information",
        description: "",
        document_link: "",
      });
      return;
    }

    try {
      // Load process tasks chain
      const chainData = await getProcessTasks(processId);
      if (Array.isArray(chainData)) {
        setChainTasks(chainData);
        if (chainData.length > 0) {
          // Initialize to the first missing phase instead of always first input
          const firstMissing = findNextMissing(0);
          const idx = firstMissing ? firstMissing.index : 0;
          const ph = firstMissing ? firstMissing.phase : "input";
          const firstTaskId = chainData[idx].task_id;
          setField("task_id", firstTaskId != null ? String(firstTaskId) : "");
          setField("position", ph);
          setCurrentIndex(idx);
          setCurrentPhase(ph);
          setExtraInputEnabled(false);
          setExtraInput({
            type: "information",
            description: "",
            document_link: "",
          });
        }
      }

      // Fetch existing assets for this process and compute coverage
      try {
        const assets = await getAssetTasksByProcessId(processId);
        const list = Array.isArray(assets) ? assets : [];
        const byTask = new Map();
        list.forEach((a) => {
          const idNum = Number(a.task_id);
          if (!Number.isInteger(idNum)) return;
          const entry = byTask.get(idNum) || { input: false, output: false };
          if (a.input) entry.input = true;
          if (a.output) entry.output = true;
          byTask.set(idNum, entry);
        });

        // Store map for quick phase checks
        const obj = {};
        byTask.forEach((v, k) => {
          obj[k] = v;
        });
        setCoverageByTask(obj);

        const total = chainData?.length || 0;
        let inputCount = 0;
        let outputCount = 0;
        let completeCount = 0;
        (chainData || []).forEach((ct) => {
          const idNum = Number(ct.task_id);
          const entry = byTask.get(idNum);
          const hasInput = !!(entry && entry.input);
          const hasOutput = !!(entry && entry.output);
          if (hasInput) inputCount++;
          if (hasOutput) outputCount++;
          if (hasInput && hasOutput) completeCount++;
        });
        setProcessCoverage({
          total,
          inputCount,
          outputCount,
          completeCount,
          isComplete: total > 0 && completeCount === total,
        });
      } catch (err) {
        // Non-blocking; just clear coverage on error
        setProcessCoverage({
          total: chainData?.length || 0,
          inputCount: 0,
          outputCount: 0,
          completeCount: 0,
          isComplete: false,
        });
        setCoverageByTask({});
      }
    } catch (e) {
      console.error("Failed to load process chain:", e);
      setChainTasks([]);
      setField("task_id", "");
      setField("position", "input");
      setCurrentIndex(0);
      setCurrentPhase("input");
      setExtraInputEnabled(false);
      setExtraInput({
        type: "information",
        description: "",
        document_link: "",
      });
      setProcessCoverage({
        total: 0,
        inputCount: 0,
        outputCount: 0,
        completeCount: 0,
        isComplete: false,
      });
      setCoverageByTask({});
    }
  };

  const onBack = () => setActiveSection("asset-task-management");

  const setField = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  // Per-task phase coverage map
  const [coverageByTask, setCoverageByTask] = useState({});

  const hasInputForTask = (taskIdNum) => {
    const entry = coverageByTask[Number(taskIdNum)];
    return !!(entry && entry.input);
  };
  const hasOutputForTask = (taskIdNum) => {
    const entry = coverageByTask[Number(taskIdNum)];
    return !!(entry && entry.output);
  };

  // Find the next missing phase across the chain starting at a given index
  const findNextMissing = (startIndex = 0) => {
    const total = Array.isArray(chainTasks) ? chainTasks.length : 0;
    for (let i = Math.max(0, startIndex); i < total; i++) {
      const tid = Number(chainTasks[i]?.task_id);
      const needInput = !hasInputForTask(tid);
      const needOutput = !hasOutputForTask(tid);
      if (needInput) return { index: i, phase: "input" };
      if (needOutput) return { index: i, phase: "output" };
    }
    return null;
  };

  const gotoNextMissingFrom = (startIndex) => {
    const next = findNextMissing(startIndex);
    if (!next) {
      toast.success("Asset task chain created successfully");
      setActiveSection("asset-task-management");
      return;
    }
    setCurrentIndex(next.index);
    const nextTaskId = chainTasks[next.index]?.task_id;
    setField("task_id", nextTaskId != null ? String(nextTaskId) : "");
    setCurrentPhase(next.phase);
    setField("type", "information");
    setField("description", "");
    setField("document_link", "");
    setExtraInputEnabled(false);
    setExtraInput({ type: "information", description: "", document_link: "" });
  };

  // Recompute banner coverage in real-time when coverageByTask or chain changes
  useEffect(() => {
    const total = Array.isArray(chainTasks) ? chainTasks.length : 0;
    if (total === 0) {
      setProcessCoverage({
        total: 0,
        inputCount: 0,
        outputCount: 0,
        completeCount: 0,
        isComplete: false,
      });
      return;
    }
    let inputCount = 0;
    let outputCount = 0;
    let completeCount = 0;
    chainTasks.forEach((ct) => {
      const idNum = Number(ct.task_id);
      const entry = coverageByTask[idNum];
      const hasInput = !!(entry && entry.input);
      const hasOutput = !!(entry && entry.output);
      if (hasInput) inputCount++;
      if (hasOutput) outputCount++;
      if (hasInput && hasOutput) completeCount++;
    });
    setProcessCoverage({
      total,
      inputCount,
      outputCount,
      completeCount,
      isComplete: total > 0 && completeCount === total,
    });
  }, [coverageByTask, chainTasks]);

  const canSaveStep = () => {
    const taskIdNum = Number(form.task_id);
    const alreadyLinked =
      (currentPhase === "input" && hasInputForTask(taskIdNum)) ||
      (currentPhase === "output" && hasOutputForTask(taskIdNum));
    // Allow proceeding if the phase is already linked (skip creation)
    if (alreadyLinked) {
      return (
        String(selectedCompanyId).trim() &&
        String(form.process_id).trim() &&
        String(form.task_id).trim() &&
        String(form.type).trim()
      );
    }
    // Otherwise require description for new creation
    return (
      String(selectedCompanyId).trim() &&
      String(form.process_id).trim() &&
      String(form.task_id).trim() &&
      String(form.type).trim() &&
      String(form.description).trim()
    );
  };

  const onStepDone = async (e) => {
    e.preventDefault();
    if (!canSaveStep() || saving) return;
    setError("");

    try {
      setSaving(true);
      const taskId = Number(form.task_id);
      const processId = form.process_id ? Number(form.process_id) : null;

      const phaseAlready =
        (currentPhase === "input" && hasInputForTask(taskId)) ||
        (currentPhase === "output" && hasOutputForTask(taskId));
      if (!phaseAlready) {
        // Save primary asset task for this phase
        const payload = {
          task_id: taskId,
          process_id: processId,
          type: form.type,
          input: currentPhase === "input",
          output: currentPhase === "output",
          description: form.description.trim(),
          document_link: form.document_link ? form.document_link.trim() : null,
        };
        await createAssetTask(payload);
        // Update coverage map
        setCoverageByTask((prev) => {
          const next = { ...prev };
          const entry = next[taskId] || { input: false, output: false };
          if (currentPhase === "input") entry.input = true;
          if (currentPhase === "output") entry.output = true;
          next[taskId] = entry;
          return next;
        });
      } else {
        toast.info(
          `Skipped: ${
            currentPhase === "input" ? "Input" : "Output"
          } already linked for Task #${taskId}`,
        );
      }

      // Optional extra input (allow on both phases if enabled)
      if (extraInputEnabled) {
        if (String(extraInput.description || "").trim()) {
          const extraPayload = {
            task_id: taskId,
            process_id: processId,
            type: extraInput.type,
            input: true,
            output: false,
            description: String(extraInput.description).trim(),
            document_link:
              extraInput.document_link ?
                String(extraInput.document_link).trim()
              : null,
          };
          await createAssetTask(extraPayload);
          // Mark coverage for input in real-time
          setCoverageByTask((prev) => {
            const next = { ...prev };
            const entry = next[taskId] || { input: false, output: false };
            entry.input = true;
            next[taskId] = entry;
            return next;
          });
        }
      }

      // Progress to next phase/index (auto-skip covered)
      if (currentPhase === "input") {
        const tid = Number(form.task_id);
        if (hasOutputForTask(tid)) {
          // Output already exists; jump to next missing task/phase
          gotoNextMissingFrom(currentIndex);
        } else {
          setCurrentPhase("output");
        }
      } else {
        const nextIndex = currentIndex + 1;
        if (nextIndex < chainTasks.length) {
          const nextTaskId = chainTasks[nextIndex]?.task_id;
          // If next input already exists, skip modal and jump ahead
          if (hasInputForTask(nextTaskId)) {
            gotoNextMissingFrom(nextIndex);
          } else {
            // Show modal for chain link confirmation before advancing
            setShowChainModal(true);
            setPendingExtraInputInModal(false);
            window.__nextChainIndex = nextIndex;
          }
        } else {
          toast.success("Asset task chain created successfully");
          setActiveSection("asset-task-management");
        }
      }
    } catch (e) {
      setError(e?.message || "Failed to save step");
      toast.error(e?.message || "Failed to save step");
    } finally {
      setSaving(false);
    }
  };

  const selectedCompany = companies.find(
    (c) => String(c.company_id) === String(selectedCompanyId),
  );

  const getTaskName = (taskId) => {
    const task = allTasks.find((t) => t.task_id === taskId || t.id === taskId);
    return task?.task_name || task?.name || `Task #${taskId}`;
  };

  const handleChainModalConfirm = async () => {
    const nextIndex = window.__nextChainIndex;
    if (nextIndex == null) return;

    try {
      // Auto-save: previous task's output becomes next task's input
      const nextTaskId = chainTasks[nextIndex].task_id;
      const processId = form.process_id ? Number(form.process_id) : null;
      if (!hasInputForTask(nextTaskId)) {
        const linkPayload = {
          task_id: nextTaskId != null ? Number(nextTaskId) : null,
          process_id: processId,
          type: form.type,
          input: true,
          output: false,
          description: String(form.description || "").trim(),
          document_link:
            form.document_link ? String(form.document_link).trim() : null,
        };
        await createAssetTask(linkPayload);
        // Mark coverage for input
        setCoverageByTask((prev) => {
          const next = { ...prev };
          const entry = next[Number(nextTaskId)] || {
            input: false,
            output: false,
          };
          entry.input = true;
          next[Number(nextTaskId)] = entry;
          return next;
        });
        toast.success("Linked previous output to next task input");
      } else {
        toast.info(`Skipped: Input already linked for Task #${nextTaskId}`);
      }

      setCurrentIndex(nextIndex);
      setField("task_id", nextTaskId != null ? String(nextTaskId) : "");
      // After linking, default to OUTPUT for the next task unless user opted to add extra input
      if (pendingExtraInputInModal) {
        setCurrentPhase("input");
        setExtraInputEnabled(true);
      } else {
        // If next task already has output, skip to the following missing step
        if (hasOutputForTask(nextTaskId)) {
          gotoNextMissingFrom(nextIndex);
        } else {
          setCurrentPhase("output");
          setExtraInputEnabled(false);
        }
      }
      setField("type", "information");
      setField("description", "");
      setField("document_link", "");
      // Allow user to add extra input for the next task if opted-in
      setExtraInputEnabled(pendingExtraInputInModal);
      setExtraInput({
        type: "information",
        description: "",
        document_link: "",
      });
    } catch (err) {
      toast.error(err?.message || "Failed to link output to next input");
    } finally {
      setShowChainModal(false);
    }
  };

  // Lock to the current task in the chain
  const currentChainTask =
    chainTasks.length > 0 ? chainTasks[currentIndex] : null;
  const availableTasks =
    currentChainTask ?
      [
        {
          task_id: currentChainTask.task_id,
          task_name:
            allTasks.find(
              (t) => (t.task_id || t.id) === currentChainTask.task_id,
            )?.task_name ||
            allTasks.find(
              (t) => (t.task_id || t.id) === currentChainTask.task_id,
            )?.name ||
            `Task #${currentChainTask.task_id}`,
        },
      ]
    : [];

  if (!roleChecked) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Loading...</div>
        </div>
      </div>
    );
  }
  if (!canAccess) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Access denied</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Create Asset Task</h2>
            <p className="page-subtitle">
              {selectedCompany ?
                `Company: ${selectedCompany.name}`
              : "Select company to continue"}
            </p>
          </div>
          <div className="roles-toolbar">
            <button
              className="secondary-btn"
              onClick={onBack}
              disabled={saving}
            >
              Back
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        {error && <div className="error-banner">{error}</div>}
        {loading ?
          <div className="no-results">Loading...</div>
        : <form onSubmit={onStepDone}>
            <div className="role-card">
              {form.process_id && (
                <div
                  style={{
                    marginBottom: 12,
                    padding: 12,
                    borderRadius: 6,
                    border: "1px solid var(--gray-200)",
                    background:
                      processCoverage.isComplete ? "var(--emerald-tint-50)" : "var(--gray-50)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>
                      {processCoverage.isComplete ?
                        "All tasks in this process already have input and output assets linked."
                      : `Coverage: input ${processCoverage.inputCount}/${processCoverage.total}, output ${processCoverage.outputCount}/${processCoverage.total}`
                      }
                    </span>
                    {!processCoverage.isComplete && (
                      <span style={{ fontSize: "0.85rem", color: "var(--gray-500)" }}>
                        Complete {processCoverage.completeCount}/
                        {processCoverage.total}
                      </span>
                    )}
                  </div>
                  {!processCoverage.isComplete && (
                    <small
                      style={{
                        display: "block",
                        marginTop: 6,
                        color: "var(--gray-500)",
                      }}
                    >
                      Continue creating to fill the missing assets.
                    </small>
                  )}
                </div>
              )}
              <div className="form-grid">
                <div className="form-group">
                  <label>
                    Company <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <select
                    value={selectedCompanyId}
                    onChange={(e) => handleCompanyChange(e.target.value)}
                    required
                  >
                    <option value="">Select Company</option>
                    {companies.map((c) => (
                      <option key={c.company_id} value={c.company_id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <small className="field-hint">
                    Select company to load associated processes and tasks
                    {companyLoading ? " (loading...)" : ""}
                  </small>
                </div>

                <div className="form-group">
                  <label>
                    Process <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <select
                    value={form.process_id}
                    onChange={(e) => handleProcessChange(e.target.value)}
                    required
                    disabled={!selectedCompanyId}
                  >
                    <option value="">Select Process</option>
                    {processes.map((p) => (
                      <option key={p.process_id} value={p.process_id}>
                        {p.process_name || p.name || `Process #${p.process_id}`}
                      </option>
                    ))}
                  </select>
                  <small className="field-hint">
                    Select process to load associated tasks
                  </small>
                </div>

                <div className="form-group">
                  <label>
                    Task <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <select
                    value={form.task_id}
                    onChange={() => {}}
                    required
                    disabled={chainTasks.length > 0}
                  >
                    <option value="">Select Task</option>
                    {availableTasks.map((t) => (
                      <option key={t.task_id} value={t.task_id}>
                        {t.task_name}
                      </option>
                    ))}
                  </select>
                  <small className="field-hint">
                    Current chain task is auto-selected and cannot be changed.
                  </small>
                </div>

                <div className="form-group">
                  <label>
                    Type <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <select
                    value={form.type}
                    onChange={(e) => setField("type", e.target.value)}
                    required
                  >
                    <option value="event">Event</option>
                    <option value="information">Information</option>
                    <option value="document">Document</option>
                  </select>
                  <small className="field-hint">
                    Event: triggers/notifications | Information: data processing
                    | Document: file generation
                  </small>
                </div>

                <div className="form-group">
                  <label>Position</label>
                  <div
                    style={{
                      padding: "10px 12px",
                      background: "var(--gray-50)",
                      border: "1px solid var(--gray-200)",
                      borderRadius: "6px",
                    }}
                  >
                    <span
                      className={`position-badge ${
                        currentPhase === "input" ? "input" : "output"
                      }`}
                    >
                      <svg
                        style={{ width: "14px", height: "14px" }}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        {currentPhase === "input" ?
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16l-4-4m0 0l4-4m-4 4h18"
                          />
                        : <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 8l4 4m0 0l-4 4m4-4H3"
                          />
                        }
                      </svg>
                      {currentPhase === "input" ? "Input" : "Output"}
                    </span>
                  </div>
                  <small className="field-hint">
                    Position auto-advances: Input then Output for each task.
                  </small>
                </div>

                {form.type === "document" && (
                  <div className="form-group">
                    <label>Document Link</label>
                    <input
                      type="url"
                      value={form.document_link}
                      onChange={(e) =>
                        setField("document_link", e.target.value)
                      }
                      placeholder="https://example.com/document.pdf"
                    />
                    <small className="field-hint">
                      Optional: URL to related document for document assets
                    </small>
                  </div>
                )}

                <div className="form-group full">
                  <label>
                    Description <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setField("description", e.target.value)}
                    rows={4}
                    placeholder="Describe this asset task..."
                    required
                  />
                  <small className="field-hint">
                    Clear description of what this asset task represents
                  </small>
                </div>
                {chainTasks.length > 0 && (
                  <div className="form-group full">
                    <label>
                      <svg
                        style={{
                          width: "16px",
                          height: "16px",
                          display: "inline",
                          marginRight: "6px",
                        }}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                      Process Task Chain
                    </label>
                    <div className="chain-display">
                      {chainTasks.map((ct, idx) => (
                        <React.Fragment key={idx}>
                          <div
                            className={`chain-step ${
                              idx === currentIndex ? "active" : ""
                            }`}
                          >
                            <div className="chain-step-number">
                              {ct.order || idx + 1}
                            </div>
                            <div className="chain-step-content">
                              <div className="chain-step-title">
                                {getTaskName(ct.task_id)}
                              </div>
                              {idx < chainTasks.length - 1 && (
                                <div className="chain-step-subtitle">
                                  Output feeds into Step {idx + 2}
                                </div>
                              )}
                            </div>
                          </div>
                          {idx < chainTasks.length - 1 && (
                            <div className="chain-arrow">↓</div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                    <small className="field-hint">
                      Tasks in this process workflow. Outputs cascade into the
                      next task's input.
                    </small>
                  </div>
                )}
              </div>
            </div>

            {currentPhase === "input" && (
              <div className="asset-form-card">
                <div className="form-group full" style={{ marginTop: 4 }}>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={extraInputEnabled}
                      onChange={(e) => setExtraInputEnabled(e.target.checked)}
                    />
                    <span style={{ marginLeft: 8 }}>
                      Add additional input for this task
                    </span>
                  </label>
                </div>
                {extraInputEnabled && (
                  <div className="form-grid" style={{ marginTop: 10 }}>
                    <div className="form-group">
                      <label>Additional Input Type</label>
                      <select
                        value={extraInput.type}
                        onChange={(e) =>
                          setExtraInput({
                            ...extraInput,
                            type: e.target.value,
                          })
                        }
                      >
                        <option value="event">Event</option>
                        <option value="information">Information</option>
                        <option value="document">Document</option>
                      </select>
                    </div>
                    {extraInput.type === "document" && (
                      <div className="form-group">
                        <label>Additional Input Document Link</label>
                        <input
                          type="url"
                          value={extraInput.document_link}
                          onChange={(e) =>
                            setExtraInput({
                              ...extraInput,
                              document_link: e.target.value,
                            })
                          }
                          placeholder="https://example.com/document.pdf"
                        />
                      </div>
                    )}
                    <div className="form-group full">
                      <label>Additional Input Description</label>
                      <textarea
                        value={extraInput.description}
                        onChange={(e) =>
                          setExtraInput({
                            ...extraInput,
                            description: e.target.value,
                          })
                        }
                        rows={3}
                        placeholder="Describe the additional input asset..."
                      />
                    </div>
                  </div>
                )}
                <small className="field-hint">
                  Optional: Saves an extra input asset for this task alongside
                  the main input.
                </small>
              </div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: 16,
              }}
            >
              <button
                className="primary-btn"
                type="submit"
                disabled={!canSaveStep() || saving}
              >
                {saving ?
                  "Saving..."
                : `Task ${currentIndex + 1} (${
                    currentPhase === "input" ? "Input" : "Output"
                  }) Done`
                }
              </button>
            </div>
          </form>
        }
      </div>

      {/* Chain Link Modal */}
      {showChainModal && (
        <div className="chain-modal-overlay">
          <div className="chain-modal">
            <div className="modal-header">
              <div className="modal-icon">
                <svg
                  style={{ width: "20px", height: "20px" }}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <h3 className="modal-title">Chain Link Confirmation</h3>
            </div>
            <div className="modal-content">
              <p style={{ marginBottom: 12 }}>
                Output from <strong>Task {currentIndex + 1}</strong> will be
                automatically used as input for{" "}
                <strong>Task {currentIndex + 2}</strong>.
              </p>
              <p style={{ fontSize: "0.9rem", color: "var(--gray-500)" }}>
                You can optionally add additional input assets for the next
                task.
              </p>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={pendingExtraInputInModal}
                  onChange={(e) =>
                    setPendingExtraInputInModal(e.target.checked)
                  }
                />
                <span style={{ marginLeft: 8 }}>
                  Add additional input to Task {currentIndex + 2}
                </span>
              </label>
              <small
                style={{ display: "block", marginTop: 4, color: "var(--gray-500)" }}
              >
                If checked, you'll be prompted to add extra input fields on the
                next screen.
              </small>
            </div>

            <div className="modal-actions">
              <button
                className="secondary-btn"
                onClick={() => setShowChainModal(false)}
              >
                Cancel
              </button>
              <button className="primary-btn" onClick={handleChainModalConfirm}>
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetTaskCreatePage;
